import { isEqual, get, cloneDeep } from 'lodash';
import { type FC, memo, useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EnvironmentFactory } from 'StorytellerEnvironment';
import StorytellerUtils from 'lib/StorytellerUtils';
import { FeatureFlags } from '@socrata/platform/feature-flags';
import { isTablePrintingImprovementForScheduledSendEnabled } from 'common/visualizations/helpers/VifSelectors';

import type { Vif as LatestVif, V1TableVif } from 'common/visualizations/vif';
import Visualization from 'common/visualizations/components/Visualization';

import {
  getGlobalFilters,
  getHasReportBeenRun,
  getParameterOverrides,
} from 'store/selectors/DataSourceSelectors';
import { updateVifWithDefaults, getDefaultVisualizationProps, vifsAreEquivalent } from 'lib/VifUtils';
import {
  applyAdditionalFiltersToVif,
  applyParameterOverridesToVif,
} from 'common/visualizations/helpers/VifHelpers';
import { type VisualizationOptions } from 'common/visualizations/views/BaseVisualization/types';
import { migrateVif } from 'common/visualizations/helpers/migrateVif';
import { filtersAreEquivalent } from 'lib/FilterUtils';
import { updateBlockComponent } from 'store/reducers/StoryReducer';
import { StorytellerReduxStoreFactory } from 'store/StorytellerReduxStore';
import { selectors as StorySelectors } from 'store/selectors/StorySelectors';
import { setFilterWhereClause, setFilterMetadata } from 'store/reducers/GridFilterReducer';
import { type FilterMetadata } from 'common/types/filterMetadata';
type PreMigrationVif = LatestVif | V1TableVif;

interface StoryVisualizationProps {
  storyVif: PreMigrationVif;
  options?: VisualizationOptions;
}

const defaultOptions = getDefaultVisualizationProps();

const enablePersonalization =
  FeatureFlags.valueOrDefault('enable_saved_state_in_stories') &&
  EnvironmentFactory().IS_STORY_PUBLISHED &&
  !EnvironmentFactory().EDIT_MODE;

// Note: You probably should NOT use this kind of hack in your own components.
// This is helpful when working with jQuery components that don't play nice with React.
const useForceRerender = () => {
  const [, setTick] = useState(0);

  return () => setTick((tick) => tick + 1);
};

// Because this component is called with ReactDOM.render, there's no way to stop a re-render even
// if the props are the same. Wrapping in `memo` lets us switch to isEqual.
// See: https://react.dev/reference/react/memo
const StoryVisualization: FC<StoryVisualizationProps> = memo(
  ({ storyVif, options }: StoryVisualizationProps) => {
    const dispatch = useDispatch();
    const additionalFilters = useSelector(getGlobalFilters, filtersAreEquivalent);
    const parameterOverrides = useSelector(getParameterOverrides, isEqual);
    const hasReportBeenRun = useSelector(getHasReportBeenRun);

    // Store the original AX filters in a ref that won't change
    const originalAxFilters = useRef(get(migrateVif(storyVif), 'series[0].dataSource.filters', []));

    const prepareVifForRender = (vif: PreMigrationVif) => {
      const migratedVif = migrateVif(vif);
      const defaultedVif = updateVifWithDefaults(migratedVif);

      // vifToRender has the ephemeral state, but we need the non-ephemeral state for filters.
      const vifWithFilters = applyAdditionalFiltersToVif(
        defaultedVif,
        additionalFilters,
        originalAxFilters.current,
      );

      return applyParameterOverridesToVif(vifWithFilters, parameterOverrides);
    };

    // If we're at this point, we know for sure that at least one of
    // storyVif, additionalFilters, or parameterOverrides has changed,
    // or we forced a re-render because of an internal viz update.

    const vifToRender = useRef<PreMigrationVif | null>(null);
    const prevStoryVif = useRef<PreMigrationVif | null>(null);
    const forceRerender = useForceRerender();
    if (!vifToRender.current || !prevStoryVif.current || !vifsAreEquivalent(prevStoryVif.current, storyVif)) {
      // Occurs when:
      // * First render in edit/view mode
      // * inserted from AX in edit mode
      // * personalization in view mode
      vifToRender.current = prepareVifForRender(storyVif);
      prevStoryVif.current = storyVif;
    } else {
      // This means either additionalFilters or parameterOverrides have changed.
      vifToRender.current = prepareVifForRender(vifToRender.current);
    }

    /**
     * This is how we preserve ephemeral state for vizes, while still merging in filter changes.
     * There's some complexity to this feature, so I'll be unnecessarily verbose.
     * All viz user interactions will emit events, but only some use `emitVifEvent`.
     * Those will bubble up to BaseVisualization, which checks `handleVifUpdatesInternally`.
     * If true, it will tell itself to rerender with `SOCRATA_VISUALIZATION_RENDER_VIF`.
     * If not, it emits `SOCRATA_VISUALIZATION_VIF_UPDATED` and expects the parent to handle it.
     * The parent is normally AX or VizCan, but now it's us, which is why we forceRender.
     * This is mainly for the QFB. If we don't re-render, the viz won't get the new filter.
     */
    const onVifUpdated = (event: any) => {
      const newVif: LatestVif = event.detail;
      const hasVifChanged = !vifsAreEquivalent(vifToRender.current as LatestVif, newVif);
      vifToRender.current = newVif;

      /**
       * This checks to make sure that the previous Vif rendered and the newVif are
       * different before triggering a dispatch that would trigger personalization
       */
      if (enablePersonalization && EnvironmentFactory().STORY_UID && hasVifChanged) {
        const StorytellerReduxStore = StorytellerReduxStoreFactory();
        // TODO EN-73613: Currently we use jQuery here to access the closest components from Story Store.
        // We'd like to move away from jQuery and use useContext once CBR is removed.
        const { blockId, componentIndex } = StorytellerUtils.findBlockIdAndComponentIndex(event.target);

        const component = StorySelectors.getBlockComponentAtIndex(
          StorytellerReduxStore.getState(),
          blockId,
          componentIndex,
        );
        // overridden filters, AKA global filters, should not be saved in the story.
        const noOverriddenFilters = newVif.series[0].dataSource.filters?.filter(
          (filter) => !filter.isOverridden,
        );
        const dispatchVif = cloneDeep(newVif);
        dispatchVif.series[0].dataSource.filters = noOverriddenFilters;
        StorytellerReduxStore.dispatch(
          updateBlockComponent({
            blockId,
            componentIndex,
            type: component.type,
            value: {
              ...component.value,
              vif: dispatchVif,
            },
          }),
        );
      }
      // If vifToRender was useState, we wouldn't need this.
      // But for this, I think useRef and forceRerender is easier to understand.
      forceRerender();
    };
    // Again, this is false because we'll receive updates here and must re-render.
    defaultOptions.drilldown.handleVifUpdatesInternally = false;

    // Pass hasReportBeenRun to prevent query execution before "Run Report" is clicked
    (defaultOptions as VisualizationOptions).hasReportBeenRun = hasReportBeenRun;

    // Create callback to pass down to GridContainer
    const handleSetFilterInfo = useCallback(
      (componentUuid: string, filterWhereClause: string | null, filterMetadata?: FilterMetadata[]) => {
        dispatch(setFilterWhereClause(componentUuid, filterWhereClause));

        // dispatch filter metadata for the reporting period feature
        if (isTablePrintingImprovementForScheduledSendEnabled() && filterMetadata) {
          dispatch(setFilterMetadata(componentUuid, filterMetadata));
        }
      },
      [dispatch],
    );

    const optionsWithDefaults = options
      ? {
          ...defaultOptions,
          ...options,
          setTableFilterInfo: handleSetFilterInfo,
        }
      : {
          ...defaultOptions,
          setTableFilterInfo: handleSetFilterInfo,
        };

    return (
      <Visualization vif={vifToRender.current} options={optionsWithDefaults} onVifUpdated={onVifUpdated} />
    );
  },
  (prevProps, nextProps) => {
    return vifsAreEquivalent(prevProps.storyVif, nextProps.storyVif);
  },
);

export default StoryVisualization;
